import type { AfterViewInit, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { BackendAllErrorCodes } from '@freelancer/datastore';
import type { Country } from '@freelancer/datastore/collections';
import { Feature, FeatureFlagsService } from '@freelancer/feature-flags';
import type { ResponseData } from '@freelancer/freelancer-http';
import type { LoginError } from '@freelancer/login-signup';
import type { Theme } from '@freelancer/theme';
import { ThemeService } from '@freelancer/theme';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { Focus } from '@freelancer/ui/focus';
import { IconSize } from '@freelancer/ui/icon';
import {
  InputComponent,
  InputEditInlineType,
  InputSize,
  InputType,
} from '@freelancer/ui/input';
import { LinkUnderline } from '@freelancer/ui/link';
import { BackgroundColor, LogoSize } from '@freelancer/ui/logo';
import type { SelectItem } from '@freelancer/ui/select';
import { SelectSize } from '@freelancer/ui/select';
import { FontWeight, TextSize, TextTransform } from '@freelancer/ui/text';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import { InvitationFlowStage } from 'app/invitation-action/invitation-action.types';
import { Observable, combineLatest, map } from 'rxjs';
import type { CredentialsFormGroup } from '../../../new-account/new-account.types';
import type { SignupDetailsFormGroup } from '../../signup.types';

@Component({
  standalone: false,
  selector: 'app-details-form-header',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsFormHeaderComponent {}

@Component({
  standalone: false,
  selector: 'app-details-form',
  template: `
    @if (showLogo) {
      <app-form-logo
        class="SignUpLogo"
        [size]="logoSize"
        [backgroundColor]="BackgroundColor.THEME"
      ></app-form-logo>
    }
    <form
      [class.Form]="!newAccountFlow"
      [class.NewAccountForm]="newAccountFlow"
      [formGroup]="formGroup"
      (ngSubmit)="onSubmit()"
    >
      @if (showHeading) {
        <div class="Redesign-headerWrapper mb-large">
          <h1
            class="text-mid Form-heading mb-small"
            i18n="Signup details heading"
          >
            <app-invitation-action
              [defaultHeading]="defaultHeading"
              [invitationFlowStage]="InvitationFlowStage.LOGIN_SIGNUP"
            ></app-invitation-action>
          </h1>
        </div>
      }
      <p
        class="text-center mb-large"
        i18n="Signup details description"
      >
        <ng-content select="app-details-form-header"></ng-content>
      </p>
      @if (showSSO) {
        @if (showGoogleButton) {
          <app-google-button
            class="GoogleButton {{ newAccountFlow ? 'mb-mid' : 'mb-small' }}"
            [currentMode]="'signup'"
            [buttonWidth]="googleButtonWidth"
            [buttonWidthTablet]="googleButtonWidthTablet"
            (signInComplete)="googleClick.emit()"
          ></app-google-button>
        }
        @if (showAppleSignin) {
          <app-login-signup-method-button
            class="{{ newAccountFlow ? 'mb-mid' : 'mb-small' }}"
            [provider]="'apple'"
            [stepLabel]="'DetailsForm'"
            (click)="appleClick.emit()"
          >
          </app-login-signup-method-button>
        }
        @if (showFacebookButton$ | flAsync) {
          <app-login-signup-method-button
            [disabled]="!facebookLoginStatusLoaded"
            class="{{ newAccountFlow ? 'mb-small' : 'mb-mid' }}"
            [provider]="'facebook'"
            [stepLabel]="'DetailsForm'"
            (click)="facebookClick.emit()"
          >
          </app-login-signup-method-button>
        }
        @if (
          (showFacebookButton$ | flAsync) || showAppleSignin || showGoogleButton
        ) {
          @if (newAccountFlow) {
            <fl-hr
              class="mb-small"
              i18n-label="credentials form divider"
              label="or"
              [textSize]="TextSize.MARKETING_SMALL"
              [textTransform]="TextTransform.INHERIT"
              [fontWeight]="FontWeight.NORMAL"
            ></fl-hr>
          } @else {
            <fl-hr
              class="mb-mid"
              i18n-label="Login form divider"
              label="or"
            ></fl-hr>
          }
        }
      }
      @if (isSignupDetailsForm(formGroup)) {
        @if (showFields$ | flAsync; as showFields) {
          @if (showFields.phone) {
            <div class="DetailsForm-marketingMobile">
              <fl-select
                flTrackingLabel="MarketingMobileNumberCountry"
                class="DetailsForm-marketingMobile-phonecode tablet:mr-xsmall mb-xxsmall tablet:mb-none"
                [control]="formGroup.controls.phoneCountry"
                [flagStartCountryCode]="
                  formGroup.controls.phoneCountry.value.code | lowercase
                "
                [id]="'selectCountry'"
                [options]="countriesList$ | flAsync"
                [size]="SelectSize.LARGE"
              ></fl-select>
              <fl-input
                class="DetailsForm-marketingMobile-phoneNumber"
                placeholder="Phone number"
                flTrackingLabel="MarketingMobileNumberMobileNumber"
                i18n-placeholder="Phone number copy"
                [control]="formGroup.controls.phoneNumber"
                [dynamicValidation]="true"
                [size]="InputSize.LARGE"
                [type]="InputType.TEXT"
              ></fl-input>
            </div>
          }
          @if (showFields.fullname) {
            <div class="DetailsForm-fullName">
              <fl-input
                flTrackingLabel="DetailsForm-FirstNameInput"
                label="First Name"
                i18n-label="First Name field label"
                placeholder="First Name"
                i18n-placeholder="Signup details first name input placeholder"
                [control]="formGroup.controls.firstName"
                [autoComplete]="'given-name'"
                [disabled]="response === null"
                [dynamicValidation]="true"
                [size]="InputSize.LARGE"
                [type]="InputType.TEXT"
              ></fl-input>
              <fl-input
                flTrackingLabel="DetailsForm-LastNameInput"
                label="Last Name"
                i18n-label="Last Name field label"
                placeholder="Last Name"
                i18n-placeholder="Signup details last name input placeholder"
                [control]="formGroup.controls.lastName"
                [autoComplete]="'family-name'"
                [disabled]="response === null"
                [dynamicValidation]="true"
                [size]="InputSize.LARGE"
                [type]="InputType.TEXT"
              ></fl-input>
            </div>
          }
          @if (showFields.company) {
            <fl-input
              flTrackingLabel="DetailsForm-CompanyNameInput"
              label="Company Name"
              i18n-label="Company Name field label"
              placeholder="Company Name (optional)"
              i18n-placeholder="Signup details company name input placeholder"
              [control]="formGroup.controls.companyName"
              [autoComplete]="'organization'"
              [disabled]="response === null"
              [dynamicValidation]="true"
              [size]="InputSize.LARGE"
              [type]="InputType.TEXT"
            ></fl-input>
          }
        }
      }
      <fl-input
        #focusInput
        flTrackingLabel="DetailsForm-EmailInput"
        label="Email"
        i18n-label="Email field label"
        [control]="formGroup.controls.email"
        [autoComplete]="'email'"
        [disabled]="response === null"
        [dynamicValidation]="true"
        [editInline]="newAccountFlow ? InputEditInlineType.BORDERED : undefined"
        class="{{ newAccountFlow ? 'mb-xxxsmall' : 'mb-none' }}"
        [labelTag]="newAccountFlow ? emailLabel : undefined"
        [placeholder]="newAccountFlow ? undefined : emailLabel"
        [size]="InputSize.LARGE"
        [type]="InputType.EMAIL"
      ></fl-input>
      <div [class.Form-password]="!newAccountFlow">
        <fl-input
          #password
          flTrackingLabel="DetailsForm-PasswordInput"
          label="Password"
          i18n-label="Password field label"
          iconEndLabel="Reveal password"
          i18n-iconEndLabel="Reveal password icon label"
          hint="Min 6 characters of lower and uppercase or a number."
          i18n-hint="Password field requirements hint"
          [control]="formGroup.controls.password"
          [autoComplete]="'current-password'"
          [disabled]="response === null"
          [dynamicValidation]="true"
          [editInline]="
            newAccountFlow ? InputEditInlineType.BORDERED : undefined
          "
          class="{{ newAccountFlow ? 'mb-xsmall' : 'mb-none' }}"
          [rightIconSize]="newAccountFlow ? IconSize.SMALL : IconSize.MID"
          [labelTag]="newAccountFlow ? passwordLabel : undefined"
          [placeholder]="newAccountFlow ? undefined : passwordLabel"
          [size]="InputSize.LARGE"
          [type]="InputType.PASSWORD"
        ></fl-input>
      </div>
      @if (isSignupDetailsForm(formGroup)) {
        @if (formGroup.controls.termsOfUse; as control) {
          <div class="Agreement">
            <fl-checkbox
              class="mb-small"
              flTrackingLabel="DetailsForm-TermsOfUseCheckbox"
              [control]="control"
              [id]="'SignupAgreement'"
            ></fl-checkbox>
            <fl-label [for]="'SignupAgreement'">
              <p
                class="text-xxsmall text-left mb-small"
                [class.text-foreground]="control.dirty && control.valid"
                [class.text-error]="control.dirty && control.invalid"
              >
                @if (!partnerName) {
                  <ng-container i18n="Signup details signup agreement label">
                    I agree to the Freelancer
                    <fl-link
                      flTrackingLabel="DetailsForm-TermsOfUseLink"
                      [link]="'/about/terms'"
                      [newTab]="true"
                      [underline]="LinkUnderline.ALWAYS"
                      [size]="TextSize.INHERIT"
                      >User Agreement</fl-link
                    >
                    and
                    <fl-link
                      flTrackingLabel="DetailsForm-PrivacyPolicyLink"
                      [link]="'/about/privacy'"
                      [newTab]="true"
                      [size]="TextSize.INHERIT"
                      [underline]="LinkUnderline.ALWAYS"
                      >Privacy Policy</fl-link
                    >.
                  </ng-container>
                } @else {
                  <ng-container i18n="Signup details signup agreement label">
                    By creating an account you agree to the use of your personal
                    information in accordance with {{ partnerName }} and
                    Freelancer.com, through the platform's
                    <fl-link
                      flTrackingLabel="DetailsForm-TermsOfUseLink"
                      [link]="'/about/terms'"
                      [newTab]="true"
                      [underline]="LinkUnderline.ALWAYS"
                      [size]="TextSize.INHERIT"
                      >Terms of Use</fl-link
                    >
                    and have read and understand the
                    <fl-link
                      flTrackingLabel="DetailsForm-PrivacyPolicyLink"
                      [link]="'/about/privacy'"
                      [newTab]="true"
                      [size]="TextSize.INHERIT"
                      [underline]="LinkUnderline.ALWAYS"
                      >Privacy Policy</fl-link
                    >
                    and
                    <fl-link
                      flTrackingLabel="DetailsForm-CookiesPolicyLink"
                      [link]="'/about/cookie'"
                      [newTab]="true"
                      [underline]="LinkUnderline.ALWAYS"
                      [size]="TextSize.INHERIT"
                      >Cookies Policy</fl-link
                    >.
                  </ng-container>
                }
              </p>
            </fl-label>
          </div>
        }
      }
      @if (response && response.status === 'error') {
        <app-login-signup-errors
          class="mb-small"
          [response]="response"
          (switchToLogin)="switchToLogin.emit()"
        ></app-login-signup-errors>
      }
      <div class="NewAccountForm-buttonContainer">
        @if (newAccountFlow) {
          <app-login-signup-button
            class="mb-small"
            i18n="Credentials form continue button"
            [busy]="response === null"
            [color]="ButtonColor.PRIMARY"
            [sizeDesktop]="ButtonSize.XLARGE"
            [sizeTablet]="ButtonSize.LARGE"
            [stepLabel]="'CredentialsForm'"
          >
            Continue
          </app-login-signup-button>
        }
      </div>
      @if (!newAccountFlow) {
        <app-login-signup-button
          class="mb-small"
          i18n="Signup details signup button"
          [busy]="response === null"
          [color]="ButtonColor.SECONDARY"
          [stepLabel]="'DetailsForm'"
        >
          @if (partnerDomain === 'Freelancer') {
            Join {{ partnerDomain }}
          }
          @if (partnerDomain !== 'Freelancer') {
            Sign Up to {{ partnerDomain }}
          }
        </app-login-signup-button>
      }
      @if (enableLoginSignupSwitch) {
        <fl-hr class="mb-small"></fl-hr>
        <p
          class="text-xxsmall text-center mb-xsmall"
          i18n="Signup details login description"
        >
          <ng-container>Already have an account</ng-container>
          @if (platformName) {
            <span class="text-foreground text-xxsmall">
              on
              <span class="text-color-inherit text-size-inherit font-bold">{{
                platformName
              }}</span></span
            >
          }
          <ng-container>? </ng-container>
          <fl-link
            flTrackingLabel="DetailsForm-LoginLink"
            [size]="TextSize.XXSMALL"
            (click)="switchToLogin.emit()"
          >
            Log in
          </fl-link>
        </p>
      }
    </form>
  `,
  styleUrls: ['./details-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsFormComponent implements AfterViewInit, OnInit {
  BackgroundColor = BackgroundColor;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  TextSize = TextSize;
  FontWeight = FontWeight;
  InputEditInlineType = InputEditInlineType;
  InputSize = InputSize;
  InputType = InputType;
  InvitationFlowStage = InvitationFlowStage;
  LinkUnderline = LinkUnderline;
  SelectSize = SelectSize;
  IconSize = IconSize;
  TextTransform = TextTransform;

  @Input() defaultHeading = $localize`Sign up`;
  @Input() logoSize = LogoSize.MID;
  @Input() countryOptions: readonly (SelectItem | string)[];
  @Input() showSSO = false;
  @Input() showAppleSignin = false;
  @Input() facebookLoginStatusLoaded = false;
  @Input() showGoogleButton = false;
  @Input() formGroup: SignupDetailsFormGroup | CredentialsFormGroup;
  @Input() enableLoginSignupSwitch = true;
  @Input() partnerName?: string;
  @Input() partnerDomain?: string;
  @Input() platformName?: string;
  @Input() promotionalAgreement: boolean;
  @Input() response?:
    | ResponseData<unknown, BackendAllErrorCodes>
    | ResponseData<unknown, LoginError>;
  @Input() showLogo = true;
  @Input() countriesList$: Observable<readonly SelectItem<Country>[]>;
  @Input() showFields$: Observable<
    | {
        readonly fullname: boolean;
        readonly company: boolean;
        readonly phone: boolean;
      }
    | false
  >;
  @Input() showHeading = true;
  @Input() newAccountFlow = false;
  @Input() googleButtonWidth = 336;
  @Input() googleButtonWidthTablet = 352;

  @Output() complete = new EventEmitter();
  @Output() appleClick = new EventEmitter();
  @Output() facebookClick = new EventEmitter();
  @Output() googleClick = new EventEmitter();
  @Output() switchToLogin = new EventEmitter();

  @ViewChild('focusInput') focusInput: InputComponent<string>;

  showFacebookButton$: Observable<boolean>;
  theme$: Observable<Theme>;
  isDesktop$: Observable<boolean>;

  passwordLabel = $localize`Password`;
  emailLabel = $localize`Email`;

  constructor(
    private featureFlagsService: FeatureFlagsService,
    private focus: Focus,
    private route: ActivatedRoute,
    private themeService: ThemeService,
  ) {}

  ngAfterViewInit(): void {
    this.focus.focusElement(this.focusInput.nativeElement);
  }

  ngOnInit(): void {
    this.showFacebookButton$ = combineLatest([
      this.featureFlagsService.getFlag(Feature.SHOW_FACEBOOK_LOGIN_SIGNUP),
      this.route.queryParamMap.pipe(
        map(queryParams => queryParams.get('showFB') === 't'),
      ),
    ]).pipe(
      map(([isfacebookEnabled, forceFacebook]) => {
        return isfacebookEnabled || forceFacebook;
      }),
    );
    this.theme$ = this.themeService.getTheme();
  }

  isSignupDetailsForm(
    formGroup: SignupDetailsFormGroup | CredentialsFormGroup,
  ): formGroup is SignupDetailsFormGroup {
    return 'termsOfUse' in formGroup.controls;
  }

  onSubmit(): void {
    if (this.response === null) {
      return;
    }
    dirtyAndValidate(this.formGroup);
    if (this.formGroup.valid) {
      this.complete.emit();
    }
  }
}
